import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import {
    computed,
    type ComputedRef,
    inject,
    type InjectionKey,
    provide,
    readonly,
    type Ref,
    ref,
    watch
} from 'vue'
import { Decimal } from '~/composables/support/decimal'
import { useShowAlert } from '#imports'
import { useI18n } from 'vue-i18n'

export const CheckoutFormQueryFragment = graphql(/* GraphQL */ `
    fragment CheckoutFormQueryFragment on Query {
        viewer {
            primaryCustomer {
                ...CustomerInformationCustomerFragment
                ...BillingAddressCustomerFragment
                ...RecurringInformationCustomerFragment
                ...PaymentInformationCustomerFragment
            }
        }
        facility(id: $facilityId) {
            ...PaymentInformationFacilityFragment
            ...RecurringInformationFacilityFragment
            ...CustomerInformationFacilityFragment
        }
    }
`)

export const CheckoutFormCheckoutFragment = graphql(/* GraphQL */ `
    fragment CheckoutFormCheckoutFragment on Checkout {
        id
        total
        dueToday
        recurringTotal
        recurringBillingRequirement
        addressRequirement
        customerInformationRequirement
        requiresRecaptcha
        items {
            __typename
            ... on CartContractItem {
                contract {
                    __typename
                    ... on PlanContract {
                        planType
                    }
                }
            }
        }
    }
`)

const CheckoutFormInjectionKey: InjectionKey<{
    checkoutId: Readonly<Ref<string | undefined>>
    processing: Readonly<Ref<number>>
    isProcessing: ComputedRef<boolean>
    incrementProcessing: () => void
    decrementProcessing: () => void
    hasGiftCardPurchase: Readonly<Ref<boolean>>
}> = Symbol('CheckoutFormInjectionKey')

export function useCheckoutForm(
    query: Ref<FragmentType<typeof CheckoutFormQueryFragment>>,
    checkout: Ref<FragmentType<typeof CheckoutFormCheckoutFragment>>
) {
    const { t } = useI18n({ useScope: 'global' })
    const showAlert = useShowAlert()
    const queryData = computed(() => {
        return getFragmentData(CheckoutFormQueryFragment, query.value)
    })

    const primaryCustomer = computed(() => {
        if (queryData.value.viewer === null) {
            return null
        }

        return queryData.value.viewer.primaryCustomer
    })

    const facility = computed(() => {
        if (queryData.value.facility === null) {
            throw new Error(t('checkout.facilityNotFound'))
        }
        return queryData.value.facility
    })

    const checkoutData = computed(() =>
        getFragmentData(CheckoutFormCheckoutFragment, checkout.value)
    )

    const checkoutId = computed(() => checkoutData.value.id)

    const requiresCustomerInformation = computed(
        () => checkoutData.value.customerInformationRequirement
    )
    // The backend owns decline history, so a refreshed checkout query controls reCAPTCHA visibility.
    const requiresRecaptcha = computed(
        () => checkoutData.value.requiresRecaptcha
    )

    const editingCustomerInformation = ref(
        requiresCustomerInformation.value ? true : false
    )
    const editingBillingAddress = ref(false)
    const editingRecurringInformation = ref(
        checkoutData.value.recurringBillingRequirement
    )
    // Require full address if Full or None specified
    const fullAddressRequired = computed(
        () =>
            checkoutData.value.addressRequirement === 'FULL' ||
            checkoutData.value.addressRequirement === 'NONE'
    )

    const total = computed(() => new Decimal(checkoutData.value.total))
    const dueToday = computed(() => new Decimal(checkoutData.value.dueToday))
    const recurringTotal = computed(
        () => new Decimal(checkoutData.value.recurringTotal)
    )
    const requiresRecurringToken = computed(
        () => checkoutData.value.recurringBillingRequirement
    )
    const hasGiftCardPurchase = computed(() => {
        // Checkout items include both direct gift-card lines and gift-card plan contracts.
        // Keep this cart-level restriction reactive if the checkout query refreshes.
        return checkoutData.value.items.some(item => {
            if (item.__typename === 'CartGiftCardItem') {
                return true
            }

            return (
                item.__typename === 'CartContractItem' &&
                item.contract.__typename === 'PlanContract' &&
                item.contract.planType === 'GIFTCARD'
            )
        })
    })

    // If they delete the last recurring item in the cart then we reload the page to get a fresh checkout
    watch(requiresRecurringToken, async (newValue, oldValue) => {
        if (newValue !== oldValue) {
            await showAlert(
                t('checkout.reloadHeader'),
                t('checkout.reloadMessage')
            )
            location.reload()
        }
    })

    const {
        processing,
        isProcessing,
        incrementProcessing,
        decrementProcessing
    } = provideCheckoutFormProcessing(checkoutId, hasGiftCardPurchase)

    return {
        queryData,
        checkoutId,
        checkoutData,

        primaryCustomer,
        facility,

        fullAddressRequired,
        requiresCustomerInformation,
        requiresRecaptcha,

        editingCustomerInformation,
        editingBillingAddress,
        editingRecurringInformation,

        total,
        dueToday,
        recurringTotal,
        requiresRecurringToken,

        processing: readonly(processing),
        isProcessing,
        incrementProcessing,
        decrementProcessing
    }
}

export function provideCheckoutFormProcessing(
    checkoutId: Readonly<Ref<string | undefined>> = ref<string>(),
    hasGiftCardPurchase: Readonly<Ref<boolean>> = ref(false)
) {
    const processing = ref(0)
    const isProcessing = computed(() => processing.value > 0)
    function incrementProcessing() {
        processing.value += 1
    }
    function decrementProcessing() {
        processing.value = Math.min(0, processing.value - 1)
    }

    provide(CheckoutFormInjectionKey, {
        // Payment providers use the durable checkout identity when creating purchase sessions.
        checkoutId,
        processing: readonly(processing),
        isProcessing,
        incrementProcessing,
        decrementProcessing,
        hasGiftCardPurchase
    })

    return {
        processing,
        isProcessing,
        incrementProcessing,
        decrementProcessing
    }
}

export function useCheckoutFormProcessing() {
    const { t } = useI18n({ useScope: 'global' })
    const injection = inject(CheckoutFormInjectionKey)
    if (injection === undefined) {
        throw new Error(t('checkout.noForm'))
    }

    return injection
}
